import React from 'react';

import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import { IconButton, ListItemIcon, Menu, MenuItem, type MenuItemProps } from '@mui/material';
import { isFunction } from 'es-toolkit';

import type { Nullable } from '@clearverify/types';

import type { DefaultDataRow, TableRowAction } from '../../../types';

interface TableRowActionsMenuProps<TRecord extends DefaultDataRow> {
  dataRow: TRecord;
  rowActions: Array<TableRowAction<TRecord>>;
}

export function TableRowActionsMenu<TRecord extends DefaultDataRow>({
  dataRow,
  rowActions,
}: TableRowActionsMenuProps<TRecord>) {
  const [anchorEl, setAnchorEl] = React.useState<Nullable<HTMLElement>>(null);

  const openMenu = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);
  const closeMenu = React.useCallback(() => setAnchorEl(null), []);

  const visibleActions = rowActions.filter(rowAction => {
    const hidden = isFunction(rowAction.hidden) ? rowAction.hidden(dataRow) : rowAction.hidden;
    return !hidden;
  });

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <React.Fragment>
      <IconButton aria-label="Row actions" aria-haspopup="menu" onClick={openMenu}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={closeMenu}>
        {visibleActions.map(rowAction => {
          const actionLabel = isFunction(rowAction.actionName)
            ? rowAction.actionName(dataRow)
            : rowAction.actionName;
          const actionDisabled = isFunction(rowAction.disabled)
            ? rowAction.disabled(dataRow)
            : rowAction.disabled;

          return (
            <TableRowActionMenuItem
              key={actionLabel}
              rowAction={rowAction}
              dataRow={dataRow}
              onSelect={closeMenu}
              disabled={actionDisabled}
              style={{ ...rowAction.style, whiteSpace: 'pre-line' }}
            >
              {rowAction.startIcon ? <ListItemIcon>{rowAction.startIcon}</ListItemIcon> : null}
              {actionLabel}
            </TableRowActionMenuItem>
          );
        })}
      </Menu>
    </React.Fragment>
  );
}

type TableRowActionMenuItemProps<TRecord extends DefaultDataRow> = MenuItemProps & {
  rowAction: TableRowAction<TRecord>;
  dataRow: TRecord;
  onSelect: () => void;
};

// `disabled` stays a prop of this element and the rest is spread onto MenuItem, so MenuList
// still sees which items to skip and can inject autoFocus/tabIndex for keyboard navigation
function TableRowActionMenuItem<TRecord extends DefaultDataRow>({
  rowAction,
  dataRow,
  onSelect,
  ...menuItemProps
}: TableRowActionMenuItemProps<TRecord>) {
  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLLIElement>) => {
      event.stopPropagation();
      onSelect();
      rowAction.onAction(dataRow);
    },
    [dataRow, onSelect, rowAction],
  );

  return <MenuItem {...menuItemProps} onClick={handleClick} />;
}
